import { useCallback, useMemo } from 'react'

import { ProductKind } from 'constants/product'
import type { ResolvedExtra, ResolvedOption, ResolvedShellProduct, SalesforceId } from 'models/purchaseFlowRevamp'
import type { CatalogueEntryRole } from 'utils/purchaseFlowRevamp/catalogueEntryRole'

import { PfRevampEventName, usePfRevampAnalytics } from '../../analytics'
import { usePfStore } from '../../store'
import { addonRoleForAnalytics, isAddonSelectedValue, nextExtraSelection } from './addonAnalytics'
import type { DrawerPricing } from './useDrawerPricing'

/**
 * Extras a configurator attaches on the client's behalf, withheld from the picker so two controls
 * never disagree over one extra: the floor-plan render-type toggle attaches `FLOOR_PLAN_EDITING_3D`,
 * the property-information page `EXTRA_LAND_REGISTRATION_SEARCH`, the living area floor counter
 * `EXTRA_CERTIFIED_FLOOR`. A line that already carries one
 * is still priced with it.
 */
const CONFIGURATOR_OWNED_EXTRA_KINDS: ReadonlySet<string> = new Set<string>([
  ProductKind.FLOOR_PLAN_EDITING_3D,
  ProductKind.EXTRA_LAND_REGISTRATION_SEARCH,
  ProductKind.EXTRA_CERTIFIED_FLOOR,
])

/** A variant's add-ons split by role, without the extras a configurator attaches itself. */
export const addonLists = (options: ResolvedOption[], extras: ResolvedExtra[]) => {
  const pickableExtras = extras.filter((extra) => !CONFIGURATOR_OWNED_EXTRA_KINDS.has(extra.productKind ?? ''))
  return {
    productOptions: options.filter((option) => option.role === 'option'),
    productExtras: pickableExtras.filter((extra) => extra.role === 'option'),
    standaloneOptions: options.filter((option) => option.role === 'standalone'),
    standaloneExtras: pickableExtras.filter((extra) => extra.role === 'standalone'),
  }
}

/**
 * The drawer's add-on lists, split by what the relation says each entry is (`catalogueEntryRole`),
 * not by the wire array it arrived in: `option` entries render as part of the chosen product,
 * `standalone` ones in the "additional products" section. Presentation only — every entry keeps
 * its own store slice and its own path into `serializePlacement`.
 *
 * The handlers wrap the store actions to report legacy's `option_selected`/`option_removed` pair;
 * the store knows an id changed, not which list the client was looking at.
 */
export const useDrawerAddons = (pricing: DrawerPricing, shell: ResolvedShellProduct | null) => {
  const shellProductId = usePfStore((s) => s.shellProductId)
  const selectedExtraIds = usePfStore((s) => s.selectedExtraIds)
  const setOptionValue = usePfStore((s) => s.setOptionValue)
  const clearOptionValue = usePfStore((s) => s.clearOptionValue)
  const toggleExtra = usePfStore((s) => s.toggleExtra)
  const trackAnalytics = usePfRevampAnalytics()

  const lists = useMemo(() => addonLists(pricing.options, pricing.extras), [pricing.options, pricing.extras])
  const pickableExtras = useMemo(() => [...lists.productExtras, ...lists.standaloneExtras], [lists])

  const announceAddon = useCallback((
    entry: { productCode: string; role: CatalogueEntryRole },
    relation: 'add_on' | 'extra_service',
    selected: boolean
  ) => {
    if (!shellProductId) return
    trackAnalytics({
      type: PfRevampEventName.ADDON_TOGGLED,
      payload: {
        shellProductId,
        productCode: entry.productCode,
        relation,
        role: addonRoleForAnalytics(entry.role),
        parentKind: shell?.kind,
        selected,
      },
    })
  }, [shellProductId, shell?.kind, trackAnalytics])

  const onSetOptionValue = useCallback((optionId: SalesforceId, value: boolean | number | string) => {
    setOptionValue(optionId, value)
    const option = pricing.options.find((candidate) => candidate.id === optionId)
    if (option) announceAddon(option, 'add_on', isAddonSelectedValue(value))
  }, [setOptionValue, pricing.options, announceAddon])

  const onClearOptionValue = useCallback((optionId: SalesforceId) => {
    clearOptionValue(optionId)
    const option = pricing.options.find((candidate) => candidate.id === optionId)
    if (option) announceAddon(option, 'add_on', false)
  }, [clearOptionValue, pricing.options, announceAddon])

  const onToggleExtra = useCallback((extraId: SalesforceId) => {
    toggleExtra(extraId)
    const extra = pickableExtras.find((candidate) => candidate.id === extraId)
    // Computed from the selection BEFORE the toggle lands, so it reports the direction the click produced.
    if (extra) announceAddon(extra, 'extra_service', nextExtraSelection(selectedExtraIds, extraId))
  }, [toggleExtra, pickableExtras, selectedExtraIds, announceAddon])

  return { ...lists, onSetOptionValue, onClearOptionValue, onToggleExtra }
}
